import React, { useCallback, useEffect, useState } from 'react';
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

// Reuses the visual language of AutoUploadSettings so both Strava-bound
// settings feel like siblings.
import './AutoUploadSettings.css';

const STRAVA_CLIENT_ID = "159733";
const STRAVA_SIGNIN_REDIRECT_URI = typeof window !== 'undefined' ? window.location.origin + "/strava-auth-callback" : "";
const STRAVA_SCOPE = "activity:read,activity:read_all,activity:write,profile:read_all";

const ChallengeProgressUploadSettings = ({ user }) => {
  // Defaults to true to mirror the legacy email recap (which was opt-out and
  // left on by most users). Existing opt-outs are carried over by a migration
  // that writes settings.challengeProgressUpload.enabled = false for users
  // with settings.challengeProgressEmails === false.
  const [enabled, setEnabled] = useState(true);
  const [hasWriteScope, setHasWriteScope] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const loadState = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const userSnap = await getDoc(doc(db, 'users', user.uid));
      const userData = userSnap.data() || {};
      const progressUpload = userData.settings?.challengeProgressUpload || {};
      setEnabled(progressUpload.enabled !== false);

      const tokenSnap = await getDoc(doc(db, 'users', user.uid, 'stravaTokens', 'tokens'));
      const scope = tokenSnap.data()?.scope || '';
      setHasWriteScope(typeof scope === 'string' && scope.includes('activity:write'));
    } catch (error) {
      console.error('Error loading challenge progress upload settings:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  const persistEnabled = async (next) => {
    if (!user) return;
    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        'settings.challengeProgressUpload.enabled': next,
      });
      setEnabled(next);
    } catch (error) {
      console.error('Error saving challenge progress upload setting:', error);
      alert('Failed to update setting. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReconnectStrava = (e) => {
    e.preventDefault();
    const state = Math.random().toString(36).substring(2);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('stravaOAuthState', state);
      const consentUrl = `https://www.strava.com/oauth/authorize?client_id=${STRAVA_CLIENT_ID}&redirect_uri=${encodeURIComponent(STRAVA_SIGNIN_REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent(STRAVA_SCOPE)}&approval_prompt=auto&state=${state}`;
      window.location.href = consentUrl;
    }
  };

  return (
    <div className="auto-upload-settings" id="challenge-progress-upload">
      <div className="auto-upload-row">
        <div className="auto-upload-info">
          <h4>Share challenge progress on Strava</h4>
          <ul className="auto-upload-bullets">
            <li>
              <span className="auto-upload-bullet-icon">🖼️</span>
              <span>Each challenge activity gets a visual progress card uploaded to its Strava entry.</span>
            </li>
            <li>
              <span className="auto-upload-bullet-icon">📝</span>
              <span>Your progress and a link to the challenge are added to the activity description.</span>
            </li>
            <li>
              <span className="auto-upload-bullet-icon">🎨</span>
              <span>Card hero is the latest checkpoint art in the challenge's style.</span>
            </li>
          </ul>
        </div>
        <div className="auto-upload-control">
          {isLoading ? (
            <div className="loading-spinner-small" />
          ) : (
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => persistEnabled(e.target.checked)}
                disabled={isSaving || !hasWriteScope}
              />
              <span className="toggle-slider" />
            </label>
          )}
        </div>
      </div>

      {!isLoading && !hasWriteScope && (
        <div className="auto-upload-scope-warning">
          <p>Reconnect Strava to share challenge progress.</p>
          <a href="/settings" onClick={handleReconnectStrava} className="auto-upload-reconnect-link">
            Reconnect Strava
          </a>
        </div>
      )}
    </div>
  );
};

export default ChallengeProgressUploadSettings;
